import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { collectionsData } from "../data/data";
import { motion } from "framer-motion";
import "../css/Collection.css";
import Footer from "./components/Footer";

function Collection({ prints, stock, setHideNavbar, isMenuOpen }) {
  const { collectionName } = useParams();
  const [collection, setCollection] = useState(null);
  const [previousCollectionName, setPreviousCollectionName] = useState("");
  const [nextCollectionName, setNextCollectionName] = useState("");
  const [direction, setDirection] = useState(0);
  const [isExiting, setIsExiting] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const navigate = useNavigate();

  const isShopAll = collectionName === "all";

  // Update collection info when collectionName changes
  useEffect(() => {
    if (!isShopAll) {
      const currentIndex = collectionsData.findIndex(
        (c) => c.name === collectionName
      );
      const prevIndex =
        currentIndex === 0 ? collectionsData.length - 1 : currentIndex - 1;
      const nextIndex =
        currentIndex === collectionsData.length - 1 ? 0 : currentIndex + 1;

      setCollection(collectionsData[currentIndex]);
      setPreviousCollectionName(collectionsData[prevIndex].name);
      setNextCollectionName(collectionsData[nextIndex].name);
    } else {
      setCollection(null);
    }
  }, [collectionName, isShopAll]);

  // Swipe animation variants
  const swipeVariants = {
    enter: (dir) => ({
      x: dir > 0 ? 300 : -300,
      opacity: 0,
      transition: { duration: 0.4 },
    }),
    center: { x: 0, opacity: 1, transition: { duration: 0.4 } },
    exit: (dir) => ({
      x: dir < 0 ? 300 : -300,
      opacity: 0,
      transition: { duration: 0.5 },
    }),
  };

  const verticalVariants = {
    enter: { y: 50, opacity: 0 },
    center: { y: 0, opacity: 1 },
    exit: { y: -50, opacity: 0 }, // optional if you want vertical exit
  };

  const handleNavigate = (targetName, mode = "dot") => {
    const currentIndex = collectionsData.findIndex(c => c.name === collectionName);
    const targetIndex = collectionsData.findIndex(c => c.name === targetName);

    let dir = 0;

    if (mode !== "swipe") {
      // Swipe direction: directly based on gesture
      dir = targetIndex > currentIndex ? 1 : -1;
    } else {
      // Dot click direction: handle wrapping more naturally
      const total = collectionsData.length;
      const forwardSteps = (targetIndex - currentIndex + total) % total;
      const backwardSteps = (currentIndex - targetIndex + total) % total;
      dir = forwardSteps <= backwardSteps ? 1 : -1;
    }

    setDirection(dir);
    setScrollY(window.scrollY);
    setIsExiting(true);

    if (window.scrollY > 0) setHideNavbar(true);

    setTimeout(() => {
      navigate(`/collections/${targetName}`);
      setIsExiting(false);
      window.scrollTo(0, 0);
      setTimeout(() => setHideNavbar(false), 200);
    }, 600);
  };

  const handlePrintClick = (printItem) => {
    const printCollection = printItem.artist;
    setIsExiting(true);
    setTimeout(() => {
      navigate(`/collections/${printCollection.name}/${printItem.index}`);
    }, 400);
  };

  const headerHeight = document.querySelector('.navbar')?.offsetHeight || 0;

  const motionStyle = isExiting && !isMenuOpen && scrollY > 0
  ? {
      position: "fixed",
      top: -(scrollY - headerHeight),
      left: 0,
      width: "100%",
      zIndex: 50,
    }
  : {};

  return (
    <div className="collection-page">
      <motion.div
        key={collectionName}
        variants={direction === 0 ? verticalVariants : swipeVariants}
        custom={direction}
        initial="enter"
        animate={isExiting ? "exit" : "center"}
        transition={{ type: "spring", stiffness: 200, damping: 25 }}
        style={motionStyle}
        drag="x"                         // enable horizontal drag
        dragConstraints={{ left: 0, right: 0 }} // no constraints
        dragElastic={0.3}                // how “springy” the drag feels
        onDragEnd={(event, info) => {
        if (isMenuOpen || isShopAll) return; // don't swipe while menu is open
        const offset = info.offset.x;
        const velocity = info.velocity.x;
        if (offset < -100 || velocity < -500) handleNavigate(nextCollectionName, "swipe");
        else if (offset > 100 || velocity > 500) handleNavigate(previousCollectionName, "swipe");
      }}
>
        <div className="collection-negative-space"></div>
        {!isShopAll && <Dots currentIndex={collectionsData.findIndex(c => c.name === collectionName)} onNavigate={handleNavigate} />}
        {!isShopAll && <ArtistBanner collection={collection} />}
        {isShopAll && <ShopAllBanner />}

        {!isShopAll ? (
          <CollectionGrid
            collection={collection}
            stock={stock}
            onPrintClick={handlePrintClick}
          />
        ) : (
          <ShopAllGrid
            prints={prints}
            stock={stock}
            onPrintClick={handlePrintClick}
          />
        )}

        {!isShopAll && <Dots currentIndex={collectionsData.findIndex(c => c.name === collectionName)} onNavigate={handleNavigate} />}

        {!isShopAll ? (
          <PreviousAndNext
            previousCollectionName={previousCollectionName}
            nextCollectionName={nextCollectionName}
            onNavigate={handleNavigate}
          />
        ) : (
          <ShopCollectionsButton />
        )}

        <Footer />
      </motion.div>
    </div>
  );
}

// --------------------------

function ArtistBanner({ collection }) {
  if (!collection) return null;
  return (
    <div className="collection-name-section frosted">
      <div className="collection-name-section-title">
        {collection.name.toUpperCase() + ' //'}
      </div>
      <div className="collection-name-section-description">
        {collection.description}
      </div>

      <div className="collection-name-swipe">Swipe for more collections.</div>
    </div>
  );
}

function Dots({ currentIndex, onNavigate }) {
  return (
    <div className="collection-dots">
      {collectionsData.map((collection, index) => (
        <span
          key={index}
          className={`dot ${index === currentIndex ? "active" : ""}`}
          onClick={() => {
            if (index !== currentIndex) onNavigate(collection.name);
          }}
          style={{ cursor: index !== currentIndex ? "pointer" : "default" }}
        />
      ))}
    </div>
  );
}

function ShopAllBanner() {
  const sectionName = "ALL PRODUCTS. ";
  const [startAnimation, setStartAnimation] = useState(false);

  useEffect(() => {
    let id;
    const start = () => setStartAnimation(true);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        id = requestAnimationFrame(start);
      });
    } else {
      id = requestAnimationFrame(start);
    }
    return () => cancelAnimationFrame(id);
  }, []);

  const getDuration = (sectionName) => sectionName.length / 0.4;

  return (
    <div className="section-title-container">
      <h1
        className="section-title"
        style={{
          animation: startAnimation
            ? `section-title-animation ${getDuration(sectionName)}s linear infinite`
            : "none",
        }}
      >
        {getRepeatedName(sectionName).toLowerCase()}
      </h1>
    </div>
  );
}

function ShopAllGrid({ prints, stock, onPrintClick }) {
  const gallery = prints
    .map((item) => {
      const itemName = `${item.artist.name}-${item.title}`
        .toLowerCase()
        .replace(/\s+/g, "-");
      return {
        item,
        collection: item.artist,
        itemStock: stock[itemName] ?? 0,
      };
    })
    .sort((a, b) => (b.itemStock > 0 ? 1 : 0) - (a.itemStock > 0 ? 1 : 0))
    .map(({ item, collection, itemStock }) => (
      <GalleryDiv
        key={`${collection.name}-${item.index}`}
        collection={collection}
        item={item}
        stock={itemStock}
        onClick={() => onPrintClick(item)}
      />
    ));

  return <div className="prints-grid">{gallery}</div>;
}

function CollectionGrid({ collection, stock, onPrintClick }) {
  const getSectionItems = useCallback(
    (sectionIndex) => {
      if (!collection) return [];
      const sectionItems = collection.items.filter(
        (item) => item.section === sectionIndex + 1
      );
      return sectionItems.sort((a, b) => {
        const aStock =
          stock[
            `${collection.name}-${a.title}`.toLowerCase().replace(/\s+/g, "-")
          ] ?? 1;
        const bStock =
          stock[
            `${collection.name}-${b.title}`.toLowerCase().replace(/\s+/g, "-")
          ] ?? 1;
        return (bStock > 0 ? 1 : 0) - (aStock > 0 ? 1 : 0);
      });
    },
    [collection, stock]
  );

  if (!collection) return null;

  const getDuration = (sectionName) => sectionName.length / 0.4;

  return collection.sections.map((sectionName, sectionIndex) => (
    <div key={sectionName}>
      <div className="section-title-container">
        <h1
          className="section-title"
          style={{
            animation: `section-title-animation ${getDuration(sectionName)}s linear infinite`,
          }}
        >
          {getRepeatedName(sectionName).toLowerCase()}
        </h1>
      </div>

      <div className="prints-grid">
        {getSectionItems(sectionIndex).map((item) => {
          const itemStock =
            stock[
              `${collection.name}-${item.title}`
                .toLowerCase()
                .replace(/\s+/g, "-")
            ] ?? 0;
          return (
            <GalleryDiv
              key={`${collection.name}-${sectionIndex}-${item.index}`}
              collection={collection}
              item={item}
              stock={itemStock}
              onClick={() => onPrintClick(item)}
            />
          );
        })}
      </div>
    </div>
  ));
}

function PreviousAndNext({ previousCollectionName, nextCollectionName, onNavigate }) {
  return (
    <div className="collection-nav-buttons">
      <div className="previous-collection">
        <div
          className="nav-button"
          onClick={() => onNavigate(previousCollectionName)}
        >
          <div className="collection-nav-buttons-flex">
            <div className="collection-nav-buttons-arrows">❮</div>
            <div>
              <div>previous</div>
              <div>collection</div>
            </div>
          </div>
        </div>
      </div>
      <div className="next-collection">
        <div
          className="nav-button"
          onClick={() => onNavigate(nextCollectionName)}
        >
          <div className="collection-nav-buttons-flex">
            <div>
              <div>next</div>
              <div>collection</div>
            </div>
            <div className="collection-nav-buttons-arrows">❯</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function GalleryDiv({ collection, item, stock, onClick }) {
  const isOutOfStock = stock === 0;

  const getPriceRange = useCallback((prices) => {
    const sizes = Object.values(prices);
    if (sizes.length === 1) return `£${sizes[0].price}`;
    let min = Infinity,
      max = 0;
    sizes.forEach((s) => {
      if (s.price > max) max = s.price;
      if (s.price < min) min = s.price;
    });
    return `£${min}-£${max}`;
  }, []);

  return (
    <motion.div
      className={`print-item`}
      onClick={onClick}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <div style={{ display: "flex", position: "relative" }}>
        <div className="print-image-container">
          <img
            src={item.mockup}
            id={item.title.replace(/ /g, "-")}
            alt={item.title}
            className={`print-image ${isOutOfStock ? "print-image-out-of-stock" : ""}`}
            loading="lazy"
          />
        </div>

        {isOutOfStock && <p className="print-unavailable-overlay">SOLD OUT</p>}
      </div>

      <div className="title-and-price-container">
        <h3 className="print-title">{item.title}</h3>
        <p className="print-price">{getPriceRange(item.prices)}</p>
      </div>
    </motion.div>
  );
}

const getRepeatedName = (sectionName) => {
  let name = sectionName;
  for (let i = 0; i < 10; i++) {
    name += "\u00A0 \u00A0 \u00A0" + sectionName;
  }
  return name;
};

function ShopCollectionsButton() {
  return (
    <div className="shop-collections-button-container">
      <Link to="/collections" className="nav-button">
        <div className="shop-collections-button">
          <div>SHOP BY ARTIST</div>
        </div>
      </Link>
    </div>
  );
}

export default Collection;